// client/src/components/influencer/InfluencerSNSCheck.js
// SNS 계정 · 단가 — A안+B안 확정 리디자인: 완료 계정은 단가 요약 카드, 미입력 계정은 펼침 편집(앰버 링)
// (조회·캐시·단가 저장·팔로워 갱신·알림 동의 로직은 기존 그대로)
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearSNSCache } from '../../utils/snsAccountCache';

const API_URL = process.env.REACT_APP_API_URL || '';

// 카테고리 고정 목록 — ⚠️ API 저장값. 배열 불변 (병기는 CATEGORY_EN 표시 맵으로만 — FEATURE_REVIEWER_UI_EN_BILINGUAL §3-3)
const CATEGORIES = [
  '뷰티/패션', '음식/맛집', '여행/라이프스타일', 'IT/테크',
  '피트니스/건강', '육아/교육', '반려동물', '기타',
];

// 카테고리 영어 병기 — 렌더 전용 맵 (저장값 아님)
const CATEGORY_EN = {
  '뷰티/패션': 'Beauty/Fashion', '음식/맛집': 'Food/Dining', '여행/라이프스타일': 'Travel/Lifestyle',
  'IT/테크': 'IT/Tech', '피트니스/건강': 'Fitness/Health', '육아/교육': 'Parenting/Education',
  '반려동물': 'Pets', '기타': 'Other',
};

// 저장 성공 메시지 — setSaveMsg와 색상 판정 비교(note--mint) 두 곳에서 동일 문자열 사용 필수
const MSG_SAVED = '저장되었습니다. (Saved)';

// 플랫폼별 단가 입력 필드 정의
const PLATFORM_RATE_FIELDS = {
  instagram: [
    { key: 'feed',  label: '피드 포스팅 (Feed post)' },
    { key: 'reels', label: '릴스 영상 (Reels)' },
  ],
  blog: [
    { key: 'post',  label: '글 포스팅 (Blog post)' },
  ],
  youtube: [
    { key: 'video',  label: '일반 영상 업로드 (Video)' },
    { key: 'shorts', label: '쇼츠 영상 (Shorts)' },
  ],
  tiktok: [
    { key: 'video', label: '영상 업로드 (Video)' },
  ],
};

const PLATFORM_LABELS = {
  instagram: '인스타그램 (Instagram)', blog: '블로그 (Blog)', youtube: '유튜브 (YouTube)', tiktok: '틱톡 (TikTok)',
};

const PLATFORM_ICONS = {
  instagram: '/images/sns/instagram.svg',
  blog:      '/images/sns/blog.svg',
  youtube:   '/images/sns/youtube.svg',
  tiktok:    '/images/sns/tiktok.svg',
};

const PLATFORM_LOGO_BG = {
  instagram: 'linear-gradient(45deg, #f09433, #dc2743)',
  blog: '#03c75a',
  youtube: '#ff0000',
  tiktok: '#111',
};

// (Instagram OAuth 인증 카드는 InfluencerSNSLink.js로 이관 — FEATURE_IG_SNS_INTEGRATION 구간 5)

// localStorage 키
const lsGroupsKey = (uk) => `influencer_sns_groups:${uk}`;
const lsRefreshKey = (uk) => `influencer_sns_refresh:${uk}`;

// 플랫폼별 fallback URL (url 미등록 계정용)
function buildFallbackUrl(platform, accountName) {
  const name = String(accountName || '').replace(/^@/, '');
  switch (platform) {
    case 'instagram': return `https://www.instagram.com/${name}`;
    case 'tiktok':    return `https://www.tiktok.com/@${name}`;
    case 'youtube':   return `https://www.youtube.com/@${name}`;
    case 'blog':      return `https://www.google.com/search?q=${encodeURIComponent(name + ' 블로그')}`;
    default:          return `https://www.google.com/search?q=${encodeURIComponent(name)}`;
  }
}

// URL 정규화: 스킴 없는 레거시 URL 보정 (예: www.instagram.com/foo → https://www.instagram.com/foo)
function normalizeUrl(url, platform, accountName) {
  const u = (url || '').trim();
  if (!u) return buildFallbackUrl(platform, accountName);
  return u.startsWith('http') ? u : `https://${u}`;
}

// 숫자 콤마 포맷
function fmtNum(n) {
  const x = Number(n || 0);
  if (x >= 100_000_000) return (x / 100_000_000).toFixed(1).replace(/\.0$/, '') + '억';
  if (x >= 10_000)      return (x / 10_000).toFixed(1).replace(/\.0$/, '') + '만';
  return x.toLocaleString();
}

// 전화번호 마스킹: 010-1234-5678 또는 01012345678 → 010-****-5678
// 주의: DB에는 하이픈 없이 저장됨(01012345678 형식)
function maskPhone(phone) {
  if (!phone) return '';
  const withHyphen = phone.replace(/^(\d{3})-(\d{3,4})-(\d{4})$/, '$1-****-$3');
  if (withHyphen !== phone) return withHyphen;
  const noHyphen = phone.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-****-$3');
  if (noHyphen !== phone) return noHyphen;
  return phone;
}

// 이메일 마스킹: abc@example.com → abc@ex***.com
function maskEmail(email) {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const [domainName, ...tld] = domain.split('.');
  const maskedDomain = domainName.slice(0, 2) + '***';
  return `${local}@${maskedDomain}.${tld.join('.')}`;
}

// 계정 단가·카테고리 설정 완료 여부 (마이비즈 배지와 동일 기준)
function isAccountComplete(account, platform) {
  const fields = PLATFORM_RATE_FIELDS[platform] || [];
  const hasRate = fields.some(({ key }) => account.desiredRate?.[key] != null && account.desiredRate[key] > 0);
  const hasCat = Array.isArray(account.categories) && account.categories.length > 0;
  return hasRate && hasCat;
}

// 계정 카드 컴포넌트 (B안: 완료 계정은 요약 + '수정' 펼침, 미완료 계정은 기본 펼침 + 앰버 링)
function AccountRateCard({ account, platform, refreshState, onRefreshRequest, onSaved }) {
  const fields = PLATFORM_RATE_FIELDS[platform] || [];
  const complete = isAccountComplete(account, platform);

  // 펼침 상태 — 미완료 계정은 기본 펼침
  const [expanded, setExpanded] = useState(!complete);

  // 단가 로컬 상태 (콤마 없이 정수 문자열로 관리)
  const [rates, setRates] = useState(() => {
    const r = {};
    for (const { key } of fields) {
      const v = account.desiredRate?.[key];
      r[key] = v != null ? String(v) : '';
    }
    return r;
  });

  // 카테고리 로컬 상태
  const [cats, setCats] = useState(() => new Set(account.categories || []));

  // 저장 후 부모에서 account prop이 갱신되면 내부 state 동기화
  // deps를 account._id만 사용: account.desiredRate가 변경될 때(다른 카드 저장 후 fetchAll)는
  // 동기화하지 않아 형제 카드의 입력 중인 값 유실을 방지함.
  // 자신의 저장 후 서버값 반영은 handleSave에서 직접 처리.
  useEffect(() => {
    const r = {};
    for (const { key } of fields) {
      const v = account.desiredRate?.[key];
      r[key] = v != null ? String(v) : '';
    }
    setRates(r);
  }, [account._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCats(new Set(account.categories || []));
  }, [account._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const handleRateChange = (key, val) => {
    // 숫자만 허용 (콤마/문자 제거)
    const clean = val.replace(/[^0-9]/g, '');
    setRates(prev => ({ ...prev, [key]: clean }));
  };

  const toggleCat = (cat) => {
    setCats(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      // 단가: 빈 문자열이면 null
      const desiredRate = {};
      for (const { key } of fields) {
        const v = rates[key];
        desiredRate[key] = v === '' ? null : Number(v);
      }

      const res = await fetch(`${API_URL}/api/influencer/mybiz/sns-rate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          snsId: account._id,
          desiredRate,
          categories: [...cats],
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        setSaveMsg(MSG_SAVED);
        // 서버 반환값으로 직접 state 갱신 (fetchAll 트리거 없이 자신의 카드 동기화)
        const serverRate = data.desiredRate || {};
        const r = {};
        let savedHasRate = false;
        for (const { key } of fields) {
          const v = serverRate[key];
          r[key] = v != null ? String(v) : '';
          if (v != null && v > 0) savedHasRate = true;
        }
        setRates(r);
        const serverCats = data.categories || [];
        setCats(new Set(serverCats)); // 서버 정규화(정렬/필터) 반영
        // 저장 결과가 완료 기준을 충족하면 요약으로 접기
        if (savedHasRate && serverCats.length > 0) setExpanded(false);
        // 캐시 무효화 후 부모에게 재계산 신호
        clearSNSCache();
        const uk = (sessionStorage.getItem('current_user_key') || '').toLowerCase();
        if (uk) localStorage.removeItem(lsGroupsKey(uk));
        if (onSaved) onSaved();
      } else {
        setSaveMsg(data.message || '저장에 실패했습니다. (Failed to save.)');
      }
    } catch {
      setSaveMsg('저장에 실패했습니다. 다시 시도해주세요. (Failed to save. Please try again.)');
    } finally {
      setSaving(false);
    }
  };

  const refreshInfo = refreshState?.[platform];
  const refreshDisabled = !!(refreshInfo?.requested);

  const handleRefresh = async () => {
    if (refreshInfo?.cooldownLeftMs > 0) {
      alert('갱신 요청은 1주일마다 가능합니다. (Refresh requests are limited to once a week.)');
      return;
    }
    if (refreshInfo?.requested) {
      alert('현재 검토 중입니다. 곧 반영해드릴게요. (Under review — it will be updated soon.)');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/influencer/mybiz/sns-refresh/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ platform }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        onRefreshRequest?.(platform);
        alert('갱신 요청이 완료되었습니다. 운영팀에서 확인 후 직접 갱신해드려요. (Request received — our team will update it after review.)');
      } else if (res.status === 429) {
        alert('갱신 요청은 1주일마다 가능합니다. (Refresh requests are limited to once a week.)');
      } else {
        alert(data.message || '요청에 실패했습니다. 다시 시도해주세요. (Request failed. Please try again.)');
      }
    } catch {
      alert('요청에 실패했습니다. 다시 시도해주세요. (Request failed. Please try again.)');
    }
  };

  // 요약 라벨 (설정된 단가만)
  const rateSummary = fields
    .filter(({ key }) => account.desiredRate?.[key] != null && account.desiredRate[key] > 0)
    .map(({ key, label }) => ({ label, value: Number(account.desiredRate[key]).toLocaleString() }));

  return (
    <div className={`snscard ${!complete ? 'snscard--warn' : ''}`}>
      {/* 카드 헤더 */}
      <div className="snscard__head">
        <span className="snscard__logo" style={{ background: PLATFORM_LOGO_BG[platform] }} aria-hidden="true">
          <img
            src={PLATFORM_ICONS[platform]}
            alt=""
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
        </span>
        <span className="snscard__meta">
          <a
            className="snscard__name"
            href={normalizeUrl(account.url, platform, account.accountName)}
            target="_blank"
            rel="noopener noreferrer"
          >
            @{account.accountName}
          </a>
          <span className="snscard__sub" style={{ display: 'block' }}>
            {PLATFORM_LABELS[platform]} · 팔로워 (Followers) {fmtNum(account.followerCount)}
          </span>
        </span>
        {account.igLinked && (
          <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 999,
            background: '#e6f9f1', color: '#0b7a54', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
          }}>
            연동 인증됨 (Connected via Instagram)
          </span>
        )}
        {complete && !expanded && (
          <button type="button" className="mb-btn mb-btn-secondary mb-btn-sm" onClick={() => setExpanded(true)}>수정 (Edit)</button>
        )}
        {!complete && <span className="badge badge--amber">단가 미입력 (No rate)</span>}
      </div>

      {/* 접힘: 단가·카테고리 요약 */}
      {!expanded && (
        <div className="snscard__rates">
          {rateSummary.map(({ label, value }) => (
            <span key={label}>{label} <b>{value}원</b></span>
          ))}
          {(account.categories || []).length > 0 && (
            <span>{account.categories.join(' · ')}</span>
          )}
        </div>
      )}

      {/* 펼침: 단가·카테고리 편집 */}
      {expanded && (
        <>
          <div className="ratebox">
            {fields.map(({ key, label }) => (
              <div key={key} className="mb-form-group">
                <label className="mb-form-label">{label} 희망 단가 (Desired rate)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="mb-form-control"
                  value={rates[key]}
                  onChange={e => handleRateChange(key, e.target.value)}
                  placeholder="예: 50,000 (e.g., 50,000)"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                />
              </div>
            ))}
          </div>

          <div className="catchips">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                type="button"
                className={`chip${cats.has(cat) ? ' is-active' : ''}`}
                onClick={() => toggleCat(cat)}
              >
                {cat} ({CATEGORY_EN[cat]})
              </button>
            ))}
          </div>

          {saveMsg && (
            <div className={`note ${saveMsg === MSG_SAVED ? 'note--mint' : 'note--red'}`}>{saveMsg}</div>
          )}

          <div className="snscard__foot">
            <span className="t-sub" style={!complete ? { color: '#9a6b00', fontWeight: 700 } : undefined}>
              {!complete ? '단가와 카테고리를 입력하면 맞는 프로젝트가 올라올 때 설정하신 방법에 따라 알림을 받을 수 있어요 (Set rates and categories to receive matching project alerts)' : '핏한 프로젝트가 올라오면 알림 드릴께요 (We will notify you of matching projects)'}
            </span>
            <button
              type="button"
              className="mb-btn mb-btn-secondary mb-btn-sm"
              onClick={handleRefresh}
              disabled={refreshDisabled}
              title={`${PLATFORM_LABELS[platform]} 팔로워 수 갱신 요청 (플랫폼 전체 계정에 적용) (Request follower refresh — applies to all accounts on this platform)`}
            >
              {refreshDisabled ? '갱신 요청 완료 (검토중) (Requested — in review)' : '팔로워 수 갱신 요청 (Refresh followers)'}
            </button>
            <button
              type="button"
              className="mb-btn mb-btn-primary mb-btn-sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '저장 중... (Saving...)' : '저장하기 (Save)'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// 메인 컴포넌트
function InfluencerSNSCheck({ onSaved }) {
  const nav = useNavigate();
  // 서버 데이터 — localStorage 선캐시로 즉시 초기 렌더 (서버 응답 전 빈 화면 방지)
  // ⚠️ 반드시 'current_user_key'를 사용할 것 (codebase 전체 표준 키)
  const [groups, setGroups] = useState(() => {
    try {
      const uk = sessionStorage.getItem('current_user_key') || '';
      if (uk) {
        const cached = localStorage.getItem(lsGroupsKey(uk));
        if (cached) return JSON.parse(cached);
      }
    } catch (_) {}
    return { instagram: [], blog: [], youtube: [], tiktok: [] };
  });
  const [refresh, setRefresh] = useState(() => {
    try {
      const uk = sessionStorage.getItem('current_user_key') || '';
      if (uk) {
        const cached = localStorage.getItem(lsRefreshKey(uk));
        if (cached) return JSON.parse(cached);
      }
    } catch (_) {}
    return {};
  });
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState('');
  const openModal = (msg) => { setModalMsg(msg); setModalOpen(true); };

  // (Instagram OAuth 연동 상태·모달은 InfluencerSNSLink.js로 이관)

  // 알림 동의 로컬 상태
  const [alertEmail, setAlertEmail] = useState(false);
  const [alertSms, setAlertSms] = useState(false);
  const [alertSaving, setAlertSaving] = useState(false);

  // 전체 계정 목록 (flat)
  const allAccounts = useMemo(() => {
    const list = [];
    for (const platform of ['instagram', 'blog', 'youtube', 'tiktok']) {
      for (const acc of (groups[platform] || [])) {
        list.push({ ...acc, platform });
      }
    }
    return list;
  }, [groups]);

  // 서버에서 데이터 로드
  const fetchAll = useCallback(async () => {
    try {
      const meRes = await fetch(`${API_URL}/api/user/me`, { credentials: 'include' });
      const mj = await meRes.json().catch(() => ({}));
      if (!meRes.ok || !mj?.ok) return;

      const uk = (mj?.user?.userId || '').toLowerCase();
      if (uk) sessionStorage.setItem('current_user_key', uk);

      const [r1, r2, r3] = await Promise.all([
        fetch(`${API_URL}/api/influencer/mybiz/sns-list`, { credentials: 'include' }),
        fetch(`${API_URL}/api/influencer/mybiz/sns-refresh/status`, { credentials: 'include' }),
        fetch(`${API_URL}/api/influencer/mybiz/profile`, { credentials: 'include' }),
      ]);

      const [d1, d2, d3] = await Promise.all([
        r1.json().catch(() => ({})),
        r2.json().catch(() => ({})),
        r3.json().catch(() => ({})),
      ]);

      if (r1.ok && d1?.ok) {
        const next = {
          instagram: d1.groups?.instagram || [],
          blog:      d1.groups?.blog      || [],
          youtube:   d1.groups?.youtube   || [],
          tiktok:    d1.groups?.tiktok    || [],
        };
        setGroups(next);
        if (uk) localStorage.setItem(lsGroupsKey(uk), JSON.stringify(next));
      }
      if (r2.ok && d2?.ok) setRefresh(d2.status || {});
      if (r3.ok && d3?.ok) {
        setProfile(d3);
        setAlertEmail(d3.projectAlertConsent?.email ?? false);
        setAlertSms(d3.projectAlertConsent?.sms ?? false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // 단가 저장 후 콜백: 데이터 재조회 + 부모 알림
  const handleSaved = useCallback(() => {
    fetchAll().then(() => {
      if (onSaved) onSaved();
    });
  }, [fetchAll, onSaved]);

  // 팔로워 갱신 요청 후 로컬 상태 갱신
  const handleRefreshRequest = useCallback((platform) => {
    setRefresh(prev => ({
      ...prev,
      [platform]: { ...prev[platform], requested: true },
    }));
    const uk = (sessionStorage.getItem('current_user_key') || '').toLowerCase();
    if (uk) {
      const key = `influencer_sns_refresh:${uk}`;
      try {
        const cached = JSON.parse(localStorage.getItem(key) || '{}');
        cached[platform] = { ...cached[platform], requested: true };
        localStorage.setItem(key, JSON.stringify(cached));
      } catch {}
    }
  }, []);

  // 알림 동의 저장
  const handleAlertSave = async () => {
    setAlertSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/mybiz/alert-consent`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: alertEmail, sms: alertSms }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        openModal('알림 설정이 저장되었습니다. (Alert settings saved.)');
      } else {
        openModal(data.message || '저장에 실패했습니다. (Failed to save.)');
      }
    } catch {
      openModal('저장에 실패했습니다. 다시 시도해주세요. (Failed to save. Please try again.)');
    } finally {
      setAlertSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-card" style={{ padding: 40, textAlign: 'center', color: '#6b7686', fontSize: 14, fontWeight: 600 }}>
        불러오는 중... (Loading...)
      </div>
    );
  }

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">SNS 계정 · 단가 관리 (SNS Accounts &amp; Rates)</h2>
        <p className="mb-page-description">
          희망 단가와 활동 분야를 입력하면 광고주 단가 통계에 반영돼요. (Your desired rates and categories feed into advertiser rate statistics.)
        </p>
      </div>

      {/* 단가 비공개 안내 */}
      {/* NOTE: "통계로만 평균 단가 확인" 문구는 현재 미구현 예정 기능 안내용. 마케터 단가 통계 기능 개발 시 실제 동작과 일치함 */}
      <div className="note note--mint" style={{ marginTop: 0, marginBottom: 14 }}>
        해당 단가는 광고주에게 안내되지 않아요. 광고주는 통계로만 평균 단가를 확인할 수 있고 크리에이터님을 단가로 특정할 수 없으니, 안심하고 희망 단가를 입력하세요. (Your rates are never shown to advertisers individually — only as aggregate statistics.)
      </div>

      {/* ── Instagram 연동 안내 — 연동 화면으로 이동 (인증 카드는 SNS 계정 연동 화면으로 이관) ── */}
      <div className="note" style={{ marginTop: 0, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span>Instagram 계정 연동(계정 소유 인증·자동 등록)은 [SNS 계정 연동] 화면에서 할 수 있어요. (Connect your Instagram account in [SNS Account Connections].)</span>
        <button
          type="button"
          className="mb-btn mb-btn-secondary mb-btn-sm"
          onClick={() => nav('/influencer/mybiz?sub=snsLink')}
        >
          SNS 계정 연동으로 이동 (Go to Connections)
        </button>
      </div>

      {/* 계정 카드 목록 */}
      {allAccounts.length === 0 ? (
        <div className="mb-card" style={{ padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#3b4658', marginBottom: 6 }}>승인된 계정이 없어요 (No approved accounts)</div>
          <div style={{ fontSize: 14, color: '#6b7686' }}>아래에서 SNS 계정을 추가해주세요 (Add an SNS account below)</div>
        </div>
      ) : (
        allAccounts.map((acc) => (
          <AccountRateCard
            key={acc._id}
            account={acc}
            platform={acc.platform}
            refreshState={refresh}
            onRefreshRequest={handleRefreshRequest}
            onSaved={handleSaved}
          />
        ))
      )}

      {/* 새 계정 추가 */}
      <button type="button" className="snscard--add" onClick={() => nav('/influencer/mybiz?sub=snsAdd')}>
        ＋ 새 SNS 계정 추가하기 — 인스타그램 · 블로그 · 유튜브 · 틱톡 (Add a new SNS account)
      </button>

      {/* 프로젝트 알림 설정 */}
      <div className="mb-card">
        <h3 className="mb-section-title" style={{ marginBottom: 8 }}>프로젝트 알림 받기 (Project Alerts)</h3>
        <p className="mb-page-description" style={{ marginBottom: 14 }}>
          설정한 카테고리에서 희망 단가 이상의 프로젝트가 올라오면 알려드려요. (Get notified when matching projects are posted.)
        </p>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, fontSize: 13.5, color: '#3b4658', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={alertEmail}
              onChange={e => setAlertEmail(e.target.checked)}
              style={{ width: 17, height: 17, accentColor: '#0fb37a' }}
            />
            이메일로 새 프로젝트 알림 (Email alerts)
            {profile?.email && <span style={{ color: '#6b7686', fontWeight: 500 }}>({maskEmail(profile.email)})</span>}
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, fontSize: 13.5, color: '#3b4658', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={alertSms}
              onChange={e => setAlertSms(e.target.checked)}
              style={{ width: 17, height: 17, accentColor: '#0fb37a' }}
            />
            문자로 새 프로젝트 알림 (SMS alerts)
            {profile?.phoneNumber && <span style={{ color: '#6b7686', fontWeight: 500 }}>({maskPhone(profile.phoneNumber)})</span>}
          </label>
          <button
            type="button"
            className="mb-btn mb-btn-secondary mb-btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={handleAlertSave}
            disabled={alertSaving}
          >
            {alertSaving ? '저장 중... (Saving...)' : '알림 설정 저장 (Save Alerts)'}
          </button>
        </div>
      </div>

      {/* 모달 */}
      {modalOpen && (
        <div className="mbm-overlay" onClick={() => setModalOpen(false)}>
          <div className="mb-card mbm" style={{ width: 360, margin: 0, textAlign: 'center', padding: '32px' }} onClick={e => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: 15.5, color: '#0b1324', lineHeight: 1.6 }}>{modalMsg}</p>
            <div className="mbm-foot">
              <button className="mb-btn mb-btn-primary" onClick={() => setModalOpen(false)}>확인 (OK)</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default InfluencerSNSCheck;
